import { useCallback } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { type RequestPolicy } from '@urql/core';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  type CustomerCreditsWithNewFieldsQuery,
  useCustomerCreditsQuery,
  useCustomerCreditsWithNewFieldsQuery,
} from './GraphQL/Credit.generated';

/**
 * Some information on credit use throughout the app.
 *
 * availableCredit: Amount of credit that is available.
 * checkoutCredit: Amount of credit applied to checkout.
 * useCredit: Whether credit should apply to checkout.
 * credits: Itemized list of operations that form the total credit amount.
 */
export const useCustomerCredit = (options?: UseCustomerCreditOptions) => {
  const { requestPolicy = 'network-only', shouldRefetchOnFocus = false } =
    options ?? {};

  const isPaytronixEnabled = useFeatureFlag('CELS-1050-enable-paytronix');
  const isScreenFocused = useIsFocused();
  const isLoggedIn = useIsLoggedIn();

  // ─────────────────────────────────────────────────────────────────────

  const customerCreditsQuery = useCustomerCreditsQuery({
    requestPolicy,
    pause:
      !isLoggedIn ||
      isPaytronixEnabled ||
      (shouldRefetchOnFocus && !isScreenFocused),
  });
  const creditsWithNewFieldsQuery = useCustomerCreditsWithNewFieldsQuery({
    requestPolicy,
    pause:
      !isLoggedIn ||
      !isPaytronixEnabled ||
      (shouldRefetchOnFocus && !isScreenFocused),
  });

  const [response, refetchCustomerCredits] = isPaytronixEnabled
    ? creditsWithNewFieldsQuery
    : customerCreditsQuery;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const refetchCredits = useCallback(() => {
    if (!isLoggedIn) return;

    refetchCustomerCredits();
  }, [isLoggedIn, refetchCustomerCredits]);

  // ─────────────────────────────────────────────────────────────────────

  const customer = response?.data?.currentCustomer;
  const { availableCredit, useCredit, credits } = customer ?? defaults;
  const creditsList = credits as Credits;

  const isLoadingCredits = response.fetching || response.stale;

  // ─────────────────────────────────────────────────────────────────────

  return {
    availableCredit,
    useCredit,
    credits: creditsList,
    isLoadingCredits,
    refetchCredits,
  };
};

// ─── Defaults ────────────────────────────────────────────────────────────────

const defaults = {
  credits: [] as Credits,
  availableCredit: 0,
  useCredit: false,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseCustomerCreditOptions = Readonly<{
  requestPolicy?: RequestPolicy;
  shouldRefetchOnFocus?: boolean;
}>;

type Credits = NonNullable<
  NonNullable<CustomerCreditsWithNewFieldsQuery['currentCustomer']>['credits']
>;
